import { IObjectKeys } from '@common/types';
import {
  ComboType,
  HealthEMIFrequency,
  HealthMemberCombo,
  HealthProductBoundary,
  HealthProductType,
  HealthQuoteMember
} from '@store/healthquote';
import { HEALTH_INSURANCE_PROVIDERS, RPED, SPED_MOD } from '@utils/constants';

export const HEALTH_QUOTE_SLUGS = {
  members: 'members',
  about: 'about',
  suggestion: 'suggestion',
  plans: 'plans',
  policy: 'policy',
  summary: 'summary',
  order: 'order'
};

export const HEALTH_FAST_QUOTE_SLUGS = {
  policy: 'policy',
  members: 'members',
  plan: 'plan',
  checkout: 'checkout'
};

export const HEALTH_PRODUCTS_SLUGS: IObjectKeys = {
  COMPANION: 'companion',
  REASSURE_3: 'reassure-3',
  REASSURE: 'reassure',
  OPTIMA_RESTORE: 'optima-restore',
  OPTIMA_SECURE: 'optima-secure',
  OPTIMA_SECURE_PLUS: 'optima-secure-plus',
  OPTIMA_SUPER_SECURE: 'optima-super-secure',
  ENERGY_SILVER: 'energy-silver',
  MEDISURETOPUP: 'medisure',
  RECHARGE: 'recharge',
  CARE_ADVANTAGE: 'care-advantage',
  CARE_FREEDOM: 'care-freedom',
  CARE_PLUS_YOUTH: 'care-plus-youth',
  CARE_PLUS_COMPLETE: 'care-plus-complete',
  CARE_SUPREME: 'care-supreme',
  CARE_ULTIMATE: 'care-ultimate',
  CARE_WITH_NCB: 'care',
  CARE_HEART: 'care-heart',
  ACTIV_ONE: 'activ-one',
  ACTIV_FIT: 'activ-fit',
  ACTIV_ONE_VYTL: 'activ-one-vytl'
};

export const HEALTH_MEMBER_COMBOS: HealthMemberCombo[] = [
  {
    name: ComboType.You,
    description: 'For self',
    members: [
      {
        relation: HealthQuoteMember.Self,
        age: undefined
      }
    ]
  },
  {
    name: ComboType.Parents,
    description: 'Your mother and father',
    members: [
      { relation: HealthQuoteMember.Father, age: undefined },
      { relation: HealthQuoteMember.Mother, age: undefined }
    ]
  },
  {
    name: ComboType.Family,
    description: 'You, your spouse, and your kids',
    members: [
      { relation: HealthQuoteMember.Self, age: undefined },
      { relation: HealthQuoteMember.Spouse, age: undefined }
    ]
  }
];

export const HEALTH_QUOTE_STEPS = {
  INTRO: 1,
  MEMBERS: 2,
  MEMBER_DETAILS: 3,
  SUGGESTION: 4,
  PLANS: 5,
  POLICY: 6,
  SUMMARY: 7,
  ORDER: 8
};

export const HEALTH_FAST_QUOTE_STEPS = {
  POLICY: 1,
  MEMBERS: 2,
  PLAN: 3,
  CHECKOUT: 4
};

export const HEALTH_QUOTE_DISEASES = [
  {
    label: 'Diabetes',
    value: 'Diabetes'
  },
  {
    label: 'BP/Hypertension',
    value: 'BP/Hypertension'
  },
  {
    label: 'Thyroid',
    value: 'Thyroid'
  },
  {
    label: 'Heart Ailments',
    value: 'Heart Ailments'
  },
  {
    label: 'Other Problems',
    value: 'Other Problems'
  }
];

export const DEFAULT_BOUNDARIES: HealthProductBoundary = {
  availableCombos: [ComboType.You, ComboType.Parents, ComboType.Family],
  maxMembers: 6,
  maxAdults: 2,
  maxKids: 4,
  type: HealthProductType.Normal,
  restrictDiabeticMembers: false,
  discounts: [],
  allowedMemberCombinations: [
    [
      HealthQuoteMember.Self,
      HealthQuoteMember.Spouse,
      HealthQuoteMember.Son,
      HealthQuoteMember.Daughter
    ]
  ],
  allowedMembers: [
    {
      relation: HealthQuoteMember.Self,
      minAge: 18,
      maxAge: 99
    },
    {
      relation: HealthQuoteMember.Spouse,
      minAge: 18,
      maxAge: 99
    },
    {
      relation: HealthQuoteMember.Son,
      minAge: 0,
      maxAge: 30
    },
    {
      relation: HealthQuoteMember.Daughter,
      minAge: 0,
      maxAge: 30
    },
    {
      relation: HealthQuoteMember.Father,
      minAge: 18,
      maxAge: 99
    },
    {
      relation: HealthQuoteMember.Mother,
      minAge: 18,
      maxAge: 99
    }
  ]
};

export const HealthMetricKeys = {
  ROOM_RENT: 'room_rent',
  RESTORATION: 'restoration',
  HEALTH_CHECKUP: 'health_checkup',
  PRE_POST: 'pre_post',
  SUB_LIMITS: 'disease_sublimit',
  DAY_CARE: 'day_care',
  AYUSH: 'ayush',
  MATERNITY: 'maternity',
  NO_CLAIM_BONUS: 'ncb',
  PED_WAITING: 'ped',
  OPD: 'opd',
  CO_PAY: 'co_pay',
  DOMICILIARY: 'domiciliary',
  LOYALTY_BONUS: 'loyalty_bonus',
  UNUTILIZED_BONUS_TRANSFER: 'ubt'
};

export const EMI_FREQUENCY_LABELS: { [key in HealthEMIFrequency]: string } = {
  [HealthEMIFrequency.Monthly]: 'Monthly',
  [HealthEMIFrequency.Quarterly]: '3 Months',
  [HealthEMIFrequency.HalfYearly]: '6 Months',
  [HealthEMIFrequency.Yearly]: '1 year'
};

export const ENABLE_EMI_PARAM = 'c2hvd0VtaQ';
export const ENABLE_EMI_PARAM_VAL = 'dHJ1ZQ';

export const ENABLE_DEDUCTIBLES_PARAM = 'c2hvd0RlZHVjdGlibGU=';
export const ENABLE_DEDUCTIBLES_VAL = 'dHJ1ZQ';

export const OPTIMA_SECURE = 'OPTIMA_SECURE';
export const OPTIMA_RESTORE = 'OPTIMA_RESTORE';

export const NON_CHRONIC = 'Non-Chronic';

export const getHealthProductDetailsForFastQuote = (product: string) => {
  const HEALTH_INSURANCE_PRODUCT_DETAILS: any = {
    REASSURE: {
      name: 'ReAssure 2.0',
      provider: HEALTH_INSURANCE_PROVIDERS.NIVABUPA
    },
    REASSURE_3: {
      name: 'ReAssure 3.0',
      provider: HEALTH_INSURANCE_PROVIDERS.NIVABUPA
    },
    COMPANION: {
      name: 'Health Companion',
      provider: HEALTH_INSURANCE_PROVIDERS.NIVABUPA
    },
    RECHARGE: {
      name: 'Health Recharge',
      provider: HEALTH_INSURANCE_PROVIDERS.NIVABUPA
    },
    OPTIMA_RESTORE: {
      name: 'Optima Restore',
      provider: HEALTH_INSURANCE_PROVIDERS.HDFCERGO
    },
    OPTIMA_SECURE: {
      name: 'Optima Secure',
      provider: HEALTH_INSURANCE_PROVIDERS.HDFCERGO
    },
    OPTIMA_SECURE_PLUS: {
      name: 'Optima Secure +',
      provider: HEALTH_INSURANCE_PROVIDERS.HDFCERGO
    },
    OPTIMA_SUPER_SECURE: {
      name: 'Optima Super Secure',
      provider: HEALTH_INSURANCE_PROVIDERS.HDFCERGO
    },
    ENERGY_SILVER: {
      name: 'Energy Silver',
      provider: HEALTH_INSURANCE_PROVIDERS.HDFCERGO
    },
    MEDISURETOPUP: {
      name: 'myHealth Medisure Super Top-Up',
      provider: HEALTH_INSURANCE_PROVIDERS.HDFCERGO
    },
    CARE_WITH_NCB: {
      name: 'Care with NCB',
      provider: HEALTH_INSURANCE_PROVIDERS.CARE
    },
    CARE_ADVANTAGE: {
      name: 'Care Advantage',
      provider: HEALTH_INSURANCE_PROVIDERS.CARE
    },
    CARE_FREEDOM: {
      name: 'Care Freedom',
      provider: HEALTH_INSURANCE_PROVIDERS.CARE
    },
    CARE_PLUS_YOUTH: {
      name: 'Care Plus - Youth health plan',
      provider: HEALTH_INSURANCE_PROVIDERS.CARE
    },
    CARE_PLUS_COMPLETE: {
      name: 'Care Plus Complete',
      provider: HEALTH_INSURANCE_PROVIDERS.CARE
    },
    CARE_SUPREME: {
      name: 'Care Supreme',
      provider: HEALTH_INSURANCE_PROVIDERS.CARE
    },
    CARE_ULTIMATE: {
      name: 'Ultimate Care',
      provider: HEALTH_INSURANCE_PROVIDERS.CARE
    },
    CARE_HEART: {
      name: 'Care Heart',
      provider: HEALTH_INSURANCE_PROVIDERS.CARE
    },
    ACTIV_ONE: {
      name: 'Activ One',
      provider: HEALTH_INSURANCE_PROVIDERS.ADITYABIRLA
    },
    ACTIV_FIT: {
      name: 'Activ Fit',
      provider: HEALTH_INSURANCE_PROVIDERS.ADITYABIRLA
    },
    ACTIV_ONE_VYTL: {
      name: 'Activ One Vytl',
      provider: HEALTH_INSURANCE_PROVIDERS.ADITYABIRLA
    }
  };

  return HEALTH_INSURANCE_PRODUCT_DETAILS[product];
};

export const HEALTH_FAST_QUOTE_FORM_ID = 'health-quote-plan-form';

export const PLAN_TYPE_HIGHLIGHT_COLORS: string[] = [
  '#FBDF00',
  '#FF80C8',
  '#56B670',
  'primary.4',
  '#8A2BE2', // Blue Violet
  '#FF7F50', // Coral
  '#00CED1', // Dark Turquoise
  '#FFD700', // Gold
  '#9370DB', // Medium Purple
  '#20B2AA' // Light Sea Green
];

// List of all possible adult relations
export const ADULT_RELATIONS: HealthQuoteMember[] = [
  HealthQuoteMember.Self,
  HealthQuoteMember.Spouse,
  HealthQuoteMember.Father,
  HealthQuoteMember.Mother,
  HealthQuoteMember.FatherInLaw,
  HealthQuoteMember.MotherInLaw
];

export const HEALTH_ADDON_ILLNESS_LISTS: {
  [addOnKey: string]: {
    listKey: 'exceptionList' | 'inclusionList';
    headerTitle: string;
    note: string;
  };
} = {
  [RPED]: {
    listKey: 'exceptionList',
    headerTitle: 'Important Note',
    note: 'For these specific illnesses (irrespective of whether they are PEDs or not), the waiting period cannot be reduced to below 2 years even with the rPED rider'
  },
  [SPED_MOD]: {
    listKey: 'inclusionList',
    headerTitle: 'Illnesses Covered',
    note: 'The reduced waiting period applies only to the following specified illnesses'
  }
};
